/**
 * cuyana-variant-collection.js
 *
 * Manages injectable marketing content placement in the variant collection grid.
 *
 * On page 1, Liquid places in-range blocks directly in the grid and puts all
 * out-of-range blocks into a hidden #injected-content-pending container with
 * data attributes describing their target position (row, col, type, columns-used).
 *
 * After each "Load More" fetch appends new product tiles, this script counts
 * the total tile-slots currently in the grid and moves any pending blocks whose
 * target position has been reached into the correct location in the grid.
 *
 * Position model (always desktop, 4 columns per row):
 *   position = (row - 1) * 4 + col
 *
 * Tile-slot counts:
 *   - product tile:      1 slot
 *   - single tile block: 1 slot
 *   - full_bleed block:  4 slots (entire row)
 *   - double_trouble:    2 slots on row N + 2 slots on row N+1 = 4 slots total
 */

(function () {
  /**
   * Count the current number of tile-slots occupied in the product grid,
   * including both product tiles and already-placed marketing content blocks.
   *
   * We walk the direct children of .ProductList in DOM order so the count
   * always reflects what is visually rendered.
   */
  function countOccupiedSlots(productList) {
    var slots = 0;
    var children = productList.children;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (child.hasAttribute('data-tile')) {
        // Product tile — occupies 1 slot
        slots += 1;
      } else {
        // Already-placed marketing content — read its columns-used
        var columnsUsed = parseInt(child.getAttribute('data-columns-used'), 10);
        if (!isNaN(columnsUsed)) {
          slots += columnsUsed;
        }
      }
    }
    return slots;
  }

  /**
   * Find the DOM child of productList before which a new block at targetPosition
   * should be inserted, based on the running slot count.
   *
   * Returns the reference child element (for insertBefore), or null to append.
   */
  function findInsertionPoint(productList, targetPosition) {
    var slots = 0;
    var children = productList.children;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];

      // If our running slot count has reached the target, insert before this child
      if (slots + 1 === targetPosition) {
        return child;
      }
      if (slots >= targetPosition) {
        return child;
      }

      if (child.hasAttribute('data-tile')) {
        slots += 1;
      } else {
        var columnsUsed = parseInt(child.getAttribute('data-columns-used'), 10);
        if (!isNaN(columnsUsed)) {
          slots += columnsUsed;
        }
      }
    }
    return null; // append at end
  }

  /**
   * Walk through all pending blocks in order of their target position and
   * place any whose position is now reachable given the current slot count.
   *
   * After placing a block we re-count from scratch so each subsequent block
   * sees an accurate slot total including the just-placed item.
   */
  function placePendingBlocks() {
    var productList = document.querySelector('.ProductList');
    var pendingContainer = document.getElementById('injected-content-pending');

    if (!productList || !pendingContainer) {
      return;
    }

    // Collect pending items sorted by their target position
    var pendingItems = Array.from(
      pendingContainer.querySelectorAll('.injected-content-pending-item')
    );

    if (pendingItems.length === 0) return;

    pendingItems.sort(function (a, b) {
      return parseInt(a.getAttribute('data-position'), 10) -
             parseInt(b.getAttribute('data-position'), 10);
    });

    var placed = true;
    // Keep looping until a full pass places nothing — handles the case where
    // placing one block enables the next to also be placed in the same pass.
    while (placed) {
      placed = false;

      for (var i = 0; i < pendingItems.length; i++) {
        var item = pendingItems[i];
        if (!item.parentNode) continue; // already removed from pending, skip

        var targetPosition = parseInt(item.getAttribute('data-position'), 10);
        var type = item.getAttribute('data-type');
        var columnsUsed = parseInt(item.getAttribute('data-columns-used'), 10);

        var currentSlots = countOccupiedSlots(productList);

        // The block is ready to place if the grid has filled up to just before
        // its target position.
        if (currentSlots < targetPosition - 1) continue;

        // Move the inner content element (the actual meta-column-block div)
        // out of its pending wrapper and into the product grid.
        var contentEl = item.firstElementChild;
        if (!contentEl) continue;

        // Transfer data attributes onto the content element so countOccupiedSlots
        // can read them after placement.
        contentEl.setAttribute('data-columns-used', columnsUsed);
        contentEl.setAttribute('data-type', type);

        var insertBefore = findInsertionPoint(productList, targetPosition);
        if (insertBefore) {
          productList.insertBefore(contentEl, insertBefore);
        } else {
          productList.appendChild(contentEl);
        }

        // Remove the now-empty pending wrapper
        item.parentNode.removeChild(item);
        pendingItems.splice(i, 1);
        i--;

        placed = true;
      }
    }
  }

  // Run on initial page load to handle any edge-case blocks that Liquid may
  // have left pending even on page 1 (e.g. a block at position 1 but capacity
  // math excluded it).
  document.addEventListener('DOMContentLoaded', function () {
    placePendingBlocks();
  });

  // Run after every Load More fetch appends new tiles.
  document.addEventListener('collection:productsAppended', function () {
    placePendingBlocks();
  });
})();
